/**
 * Shared Meta conversion ids. Browser (GTM pixel) and server (CAPI) must
 * emit the same event_name + event_id or Meta counts the conversion twice.
 *
 * The pixel path here is always-on and does not read CAPI secrets. CAPI is
 * optional: when it is not configured, these dataLayer pushes still fire.
 *
 * Lives in a client-safe module so signup/checkout can call buildEventId
 * without importing the server CAPI client.
 */

export type MetaEventName =
  | "CompleteRegistration"
  | "StartTrial"
  | "Subscribe";

/**
 * GTM custom-event names already used as triggers. Do not rename these
 * without updating the GTM container. The Facebook event name Meta
 * dedupes on is `metaEventName`, not this trigger.
 *
 * `CompleteRegistration` (not `Lead`) because the GTM pixel tag has always
 * fired that name and the live ad set optimises on it; Meta deduplicates
 * only when event_name matches on both sides, so the server follows the
 * pixel, never the other way round.
 */
export const META_DATALAYER_EVENT: Record<MetaEventName, string> = {
  CompleteRegistration: "sign_up",
  StartTrial: "trial_start",
  Subscribe: "purchase",
};

/**
 * Dedup key. Meta merges a browser and a server event when the pixel's
 * eventID equals this event_id AND the event names match.
 *
 * Seeded on the user id, deliberately NOT on the journey click id: an mcid
 * is per tab-session and never cleared from localStorage, so two people
 * signing up on one device share one — and a shared event_id is exactly
 * what makes Meta drop the second, real conversion.
 */
export function buildEventId(eventName: MetaEventName, userId: string): string {
  return `${userId}-${eventName}`;
}

export type MetaDataLayerExtra = Record<string, string | number>;

/**
 * Fire the browser Meta Pixel via GTM. Always pushes the trigger — CAPI
 * being unset, broken, or skipped must not suppress this. When CAPI is
 * also running, the same event_id lets Meta dedupe the pair.
 *
 * No-ops on the server. Skips the id (but still pushes the trigger) when
 * userId is missing — Meta cannot dedupe that hit.
 */
export function pushMetaPixelEvent(
  eventName: MetaEventName,
  userId: string | null | undefined,
  extra?: MetaDataLayerExtra
): void {
  if (typeof window === "undefined") return;

  const eventId = userId?.trim()
    ? buildEventId(eventName, userId.trim())
    : undefined;
  const target = window as unknown as { dataLayer?: unknown[] };
  target.dataLayer = target.dataLayer || [];
  target.dataLayer.push({
    ...(extra ?? {}),
    event: META_DATALAYER_EVENT[eventName],
    metaEventName: eventName,
    ...(eventId ? { eventID: eventId, event_id: eventId } : {}),
  });
}
